import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { UserPreferencesContext } from '@/context/UserPreferencesContext';

import { readAccountWalkthroughs, rememberAccountWalkthroughs, subscribeAccountWalkthroughs, accountWalkthroughKey } from './walkthroughHistory';

/** Wait for this account's server read before deciding whether to interrupt it. */
export function useAccountWalkthroughs() {
  const { user } = useAuth();
  const context = useContext(UserPreferencesContext);
  const latest = useRef(context);
  latest.current = context;
  const userId = user?.id;
  const [, refreshHistory] = useState(0);
  useEffect(() => {
    if (!userId) return;
    const changed = () => refreshHistory(n => n + 1);
    const unsubscribe = subscribeAccountWalkthroughs(userId, changed);
    const storageChanged = (event: StorageEvent) => { if (event.key === accountWalkthroughKey(userId) || event.key === null) changed(); };
    window.addEventListener('storage', storageChanged);
    return () => { unsubscribe(); window.removeEventListener('storage', storageChanged); };
  }, [userId]);
  const ready = !!userId && context?.loadedForUserId === userId;
  const hasSeen = useCallback((id: string) => {
    return !!userId && (readAccountWalkthroughs(userId).includes(id) || latest.current?.preferences?.walkthroughs_seen?.includes(id) === true);
  }, [userId]);
  const markSeen = useCallback((id: string) => {
    if (!userId || latest.current?.loadedForUserId !== userId) return;
    const remote = latest.current.preferences?.walkthroughs_seen || [];
    const ids = [...new Set([...remote, ...readAccountWalkthroughs(userId), id])];
    rememberAccountWalkthroughs(userId, ids);
    if (ids.some(seen => !remote.includes(seen))) {
      void latest.current.updatePreferences({ walkthroughs_seen: ids }).catch(error => {
        console.warn('[Walkthrough] Account save failed; keeping the local dismissal for retry.', error);
      });
    }
  }, [userId]);
  useEffect(() => {
    if (!ready || !userId) return;
    const flush = () => {
      const remote = latest.current?.preferences?.walkthroughs_seen || [];
      const pending = readAccountWalkthroughs(userId).filter(id => !remote.includes(id));
      if (pending.length) markSeen(pending[0]); // merges every locally remembered id
    };
    flush();
    window.addEventListener('online', flush);
    window.addEventListener('focus', flush);
    return () => { window.removeEventListener('online', flush); window.removeEventListener('focus', flush); };
  }, [ready, userId, markSeen]);
  return { ready, hasSeen, markSeen };
}
